/**
 * Native quick-add — Phase 1 of ConvertCart replacement (2026-05-24).
 *
 * Fetches the full product page HTML, extracts the product-information block
 * (everything above the fold on the PDP: title, price, USP bullets, variant
 * picker, ATC, etc.), injects it into our dialog, then re-initialises the
 * variant picker and intercepts form submission for AJAX cart add.
 */
(function () {
  'use strict';

  const dialogId = 'aro-quick-add-dialog';
  let dialog = null;
  let body = null;
  let returnFocusEl = null;
  let isOpening = false;
  // Last priced variant, so we can re-render the price when the VAT selector toggles.
  let lastVariant = null;
  let lastProductData = null;
  // Per-session cache of fetched HTML + product.js. Filled by hover prefetch
  // OR by the first click on an un-prefetched button. Read by openModal so the
  // 2nd open of the same product is instant.
  const productCache = new Map();

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    dialog = document.getElementById(dialogId);
    console.info('[aro-quick-add] init — dialog found:', !!dialog);
    if (!dialog) return;
    body = dialog.querySelector('[data-aro-qa-body]');

    // Capture-phase listener so we run BEFORE any <product-card> parent handler.
    document.addEventListener('click', handleTriggerClick, true);
    document.querySelectorAll('[data-aro-quick-add]').forEach(function (btn) {
      btn.addEventListener('click', handleTriggerClick, true);
    });

    // 2026-05-25: prefetch on hover / touchstart so the modal pops instantly.
    document.addEventListener('mouseover', handlePrefetchHint);
    document.addEventListener('touchstart', handlePrefetchHint, { passive: true });

    // Close on the X button. We deliberately don't close on backdrop click —
    // the open-click was bubbling into the dialog and closing it instantly.
    dialog.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-aro-qa-close]')) closeModal();
    });
    dialog.addEventListener('cancel', function (e) {  // ESC key
      e.preventDefault();
      closeModal();
    });

    // Re-price the open modal when the VAT selector changes (Taxify fires taxify:mode-changed;
    // the header pill fires custom-vat-mode-changed when Taxify isn't present).
    document.addEventListener('taxify:mode-changed', onVatModeChange);
    document.addEventListener('custom-vat-mode-changed', onVatModeChange);
  }

  function onVatModeChange() {
    if (dialog && (dialog.open || dialog.hasAttribute('open')) && lastVariant) {
      updateModalPrice(lastVariant, lastProductData);
    }
  }

  function handlePrefetchHint(e) {
    const btn = e.target.closest && e.target.closest('[data-aro-quick-add]');
    if (!btn || btn._aroPrefetched) return;
    btn._aroPrefetched = true;
    const handle = btn.getAttribute('data-aro-quick-add');
    if (!handle || productCache.has(handle)) return;
    fetchProductData(handle).then(function (data) {
      if (data) productCache.set(handle, data);
    });
  }

  async function fetchProductData(handle) {
    try {
      // Full product page HTML + product.js in parallel. We need the full page
      // (not section_id) so we have the INCI tab (#tab-desc-4) markup available.
      const [htmlR, jsonR] = await Promise.all([
        fetch('/products/' + handle, { credentials: 'same-origin' }),
        fetch('/products/' + handle + '.js', { credentials: 'same-origin' }),
      ]);
      if (!htmlR.ok) return null;
      const html = await htmlR.text();
      const productData = jsonR.ok ? await jsonR.json() : null;
      return { html: html, productData: productData };
    } catch (_) {
      return null;
    }
  }

  function handleTriggerClick(e) {
    const trigger = e.target.closest && e.target.closest('[data-aro-quick-add]');
    if (!trigger) return;
    console.info('[aro-quick-add] trigger click intercepted, handle:', trigger.getAttribute('data-aro-quick-add'));
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    const handle = trigger.getAttribute('data-aro-quick-add');
    if (handle) openModal(handle, trigger);
  }

  async function openModal(handle, trigger) {
    if (isOpening) return;
    isOpening = true;
    returnFocusEl = trigger;

    body.innerHTML = '<div class="aro-quick-add__loading">Loading…</div>';
    if (typeof dialog.showModal === 'function') {
      try { dialog.showModal(); } catch (_) { dialog.setAttribute('open', ''); }
    } else {
      dialog.setAttribute('open', '');
    }

    try {
      // Reuse hover-prefetched data if present, else fetch now.
      let cached = productCache.get(handle);
      if (!cached) {
        cached = await fetchProductData(handle);
        if (cached) productCache.set(handle, cached);
      }
      if (!cached || !cached.html) throw new Error('product data unavailable');

      const doc = new DOMParser().parseFromString(cached.html, 'text/html');

      // Extract the product info column. Try a few selectors so this works on
      // both the default product template and the custom new-pdp template.
      const selectors = [
        '.product-information',
        '[id^="ProductInfo"]',
        'sticky-scroller.product-information',
      ];
      let productInfo = null;
      for (const s of selectors) {
        productInfo = doc.querySelector(s);
        if (productInfo) break;
      }
      if (!productInfo) throw new Error('Product info block not found in HTML');

      const productData = cached.productData;

      // Build wrapper: fixed-left gallery + scrollable-right info column.
      const wrapper = document.createElement('div');
      wrapper.className = 'aro-quick-add__pdp';

      // Gallery column
      wrapper.appendChild(buildGallery(productData, doc));

      // Info column (this is the only scrollable bit)
      const infoCol = document.createElement('div');
      infoCol.className = 'aro-quick-add__info';
      infoCol.appendChild(productInfo);

      // Trim "Still deciding?" + below before adding the View Product CTA so we
      // measure off the trimmed tree.
      trimFromStillDeciding(productInfo);

      // View Product outline CTA next to the ATC
      addViewProductLink(infoCol, handle);

      // 2026-05-25: INCI / Ingredients excerpt from PDP tab-desc-4.
      addInciExcerpt(infoCol, doc);

      wrapper.appendChild(infoCol);

      body.innerHTML = '';
      body.appendChild(wrapper);

      // Re-initialise the variant picker. custom-variant-picker.js exposes
      // initializeVariantPicker at script scope; modern browsers also expose
      // top-level function decls on window.
      const vsList = body.querySelectorAll('variant-selects');
      vsList.forEach(function (vs) {
        if (typeof window.initializeVariantPicker === 'function') {
          try { window.initializeVariantPicker(vs); } catch (_) {}
        }
      });

      // Run the cached unit-price re-render so US imperial / VAT-mode rules
      // apply inside the modal too (renderVariantCards reads from theme.settings).
      if (productData && typeof window.renderVariantCards === 'function') {
        try { window.renderVariantCards(productData, body); } catch (_) {}
      }

      // Variant change handler — keep the main price block in sync with the
      // selected variant (PDP's variant-change script doesn't fire in modal).
      if (productData) {
        setupVariantChangeListener(productData);
        // Render the price block immediately with the default variant so the
        // visitor sees our preferred order from the first frame (Price → From → Save).
        const initial = productData.variants.find(function (v) { return v.available; }) || productData.variants[0];
        if (initial) updateModalPrice(initial, productData);
        // Preorder / backorder despatch message for the initial variant. Scope the
        // lookup to .custom-variant-card so we don't grab the Swym wishlist button
        // (it carries the same data-variant-id).
        if (initial) {
          updateModalPreorderMessage(
            body.querySelector('.custom-variant-card[data-variant-id="' + initial.id + '"]')
          );
        }
        // Per-variant percentage-off stickers
        if (typeof window.addVariantSaleBadges === 'function') {
          try { window.addVariantSaleBadges(productData, body); } catch (_) {}
        }
      }

      // Intercept the add-to-cart form so submission stays in the modal.
      const form = body.querySelector('form[action*="/cart/add"]');
      if (form) form.addEventListener('submit', handleAtcSubmit);
    } catch (err) {
      body.innerHTML = `<div class="aro-quick-add__error">Couldn't load this product. <a href="/products/${handle}">View product page</a> instead.</div>`;
    } finally {
      isOpening = false;
    }
  }

  function buildGallery(productData, doc) {
    const el = document.createElement('div');
    el.className = 'aro-quick-add__gallery';

    // Prefer product.js images array (always full list, full size)
    let imageUrls = [];
    if (productData && Array.isArray(productData.images) && productData.images.length) {
      imageUrls = productData.images;
    } else {
      // Fallback — scrape any image from the PDP HTML
      const img = doc.querySelector('product-gallery img, .product-media img, .product-images img, [class*="featured"] img');
      if (img && img.src) imageUrls = [img.src];
    }
    if (!imageUrls.length) {
      el.innerHTML = '<div class="aro-quick-add__gallery-empty">No image</div>';
      return el;
    }

    let idx = 0;
    const alt = (productData && productData.title) || '';
    el.innerHTML = `
      <div class="aro-quick-add__gallery-viewport">
        <img class="aro-quick-add__gallery-img" src="${imageUrls[0]}" alt="${escapeAttr(alt)}">
      </div>
      ${imageUrls.length > 1 ? `
        <button type="button" class="aro-quick-add__gallery-nav aro-quick-add__gallery-nav--prev" data-aro-qa-prev aria-label="Previous image">&lsaquo;</button>
        <button type="button" class="aro-quick-add__gallery-nav aro-quick-add__gallery-nav--next" data-aro-qa-next aria-label="Next image">&rsaquo;</button>
        <div class="aro-quick-add__gallery-counter" data-aro-qa-counter>1 / ${imageUrls.length}</div>
      ` : ''}
    `;

    if (imageUrls.length > 1) {
      const imgEl = el.querySelector('.aro-quick-add__gallery-img');
      const counter = el.querySelector('[data-aro-qa-counter]');
      const setIdx = (n) => {
        idx = ((n % imageUrls.length) + imageUrls.length) % imageUrls.length;
        imgEl.src = imageUrls[idx];
        if (counter) counter.textContent = `${idx + 1} / ${imageUrls.length}`;
      };
      el.querySelector('[data-aro-qa-prev]').addEventListener('click', (e) => { e.preventDefault(); setIdx(idx - 1); });
      el.querySelector('[data-aro-qa-next]').addEventListener('click', (e) => { e.preventDefault(); setIdx(idx + 1); });
    }

    // Pre-order banner — the modal builds its own gallery, so the server-rendered
    // .preorder-banner from the PDP slider isn't otherwise carried over. Clone it
    // from the fetched PDP doc so the quick view matches the card + PDP (same notched
    // discount-banner pill). The base .discount-banner rule pins it top-left and
    // .aro-quick-add__gallery is position:relative, so it lands top-left of the
    // gallery column (pill sized via .aro-quick-add__gallery .discount-banner__pill).
    const preorderBanner = doc && doc.querySelector('.preorder-banner');
    if (preorderBanner) {
      el.appendChild(preorderBanner.cloneNode(true));
    }
    // Best-seller banner — same rationale as the pre-order clone above. Lands
    // top-right of the gallery column (sized via .aro-quick-add__gallery .bestseller-banner).
    const bestsellerBanner = doc && doc.querySelector('.bestseller-banner');
    if (bestsellerBanner) {
      el.appendChild(bestsellerBanner.cloneNode(true));
    }
    return el;
  }

  function addInciExcerpt(infoCol, doc) {
    let inciHtml = '';

    // Approach 1: find the tab button whose label contains "INCI" / "Ingredients",
    // then jump to the panel its data-tab points at. The position of the INCI tab
    // varies per product (tab-desc-N indices aren't consistent).
    const buttons = doc.querySelectorAll('.custom-product-tabs__button');
    for (const btn of buttons) {
      const text = (btn.textContent || '').trim();
      if (/inci|ingredient/i.test(text)) {
        const dataTab = btn.getAttribute('data-tab');
        if (dataTab) {
          const panel = doc.querySelector('#' + dataTab);
          if (panel && panel.textContent.trim().length > 10) {
            inciHtml = panel.innerHTML;
            break;
          }
        }
      }
    }

    // Approach 2: any heading starting with "INCI:" — wrap in its closest
    // rich-text container so we get the ingredient list alongside the label.
    if (!inciHtml) {
      const headings = doc.querySelectorAll('h1, h2, h3, h4');
      for (const h of headings) {
        if (/^\s*INCI:/i.test(h.textContent || '')) {
          const block = h.closest('.shg-rich-text, .rte, .custom-product-tabs__panel') || h.parentElement;
          if (block) { inciHtml = block.innerHTML; break; }
        }
      }
    }

    if (!inciHtml) return;

    const block = document.createElement('details');
    block.className = 'aro-qa-inci';
    block.innerHTML =
      '<summary class="aro-qa-inci__summary">INCI / Ingredients</summary>' +
      '<div class="aro-qa-inci__body">' + inciHtml + '</div>';

    const form = infoCol.querySelector('form[action*="/cart/add"]');
    const viewLink = infoCol.querySelector('.aro-quick-add__view-product');
    if (viewLink) viewLink.insertAdjacentElement('afterend', block);
    else if (form) form.insertAdjacentElement('afterend', block);
    else infoCol.appendChild(block);
  }

  function addViewProductLink(infoCol, handle) {
    // Insert outline "View Product" link directly after the ATC form so it sits
    // visually next to the Add to Cart button on the right column.
    const form = infoCol.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    const link = document.createElement('a');
    link.className = 'aro-quick-add__view-product';
    link.href = `/products/${handle}`;
    link.textContent = 'View Product';
    form.insertAdjacentElement('afterend', link);
  }

  function escapeAttr(s) {
    return String(s == null ? '' : s).replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function setupVariantChangeListener(productData) {
    body.addEventListener('click', function (e) {
      const card = e.target.closest && e.target.closest('.custom-variant-card');
      if (!card) return;
      // Small delay so the variant picker JS has had a tick to update .selected
      setTimeout(function () {
        const form = body.querySelector('form[action*="/cart/add"]');
        const variantId = resolveSelectedVariantId(form || body);
        const variant = productData.variants.find(function (v) { return String(v.id) === String(variantId); });
        if (variant) updateModalPrice(variant, productData);
        const selCard =
          body.querySelector('.custom-variant-card.selected') ||
          (variant && body.querySelector('.custom-variant-card[data-variant-id="' + variant.id + '"]'));
        updateModalPreorderMessage(selCard);
      }, 100);
    });
  }

  // Preorder / backorder despatch note — mirrors the PDP. The despatch text lives
  // on each variant card as data-preorder-message / data-backorder-message (with
  // data-availability), which the extracted .product-information block doesn't
  // surface, so we render our own block right above the Add-to-Cart CTA. Only
  // shows for purchasable-but-not-in-stock states; truly out-of-stock = none.
  function updateModalPreorderMessage(card) {
    let el = body.querySelector('.aro-qa-preorder');
    const ds = card && card.dataset ? card.dataset : null;
    const avail = ds ? ds.availability : '';
    const msg = ds ? (ds.preorderMessage || ds.backorderMessage || '') : '';
    const show = (avail === 'pre-order' || avail === 'back-order') && msg;
    if (!show) {
      if (el) el.style.display = 'none';
      return;
    }
    if (!el) {
      el = document.createElement('div');
      el.className = 'aro-qa-preorder';
    }
    // Place it at the very bottom — directly below the "View Product" link — so it
    // reads like the PDP's availability message under the buy box. Fall back to the
    // end of the info column if that link isn't present.
    const viewProduct = body.querySelector('.aro-quick-add__view-product');
    if (viewProduct && viewProduct.parentNode) {
      viewProduct.parentNode.insertBefore(el, viewProduct.nextSibling);
    } else if (!el.parentNode) {
      const slot =
        body.querySelector('.aro-quick-add__info') ||
        body.querySelector('.product-information--inner') ||
        body.querySelector('.product-information');
      if (slot) slot.appendChild(el);
      else return;
    }
    el.textContent = msg;
    el.style.display = '';
  }

  function updateModalPrice(variant, productData) {
    lastVariant = variant;
    lastProductData = productData;
    // 2026-05-25: rather than fight Taxify (which keeps re-injecting save/from
    // elements into the original .product--price-container), we hide the
    // originals entirely and render our own dedicated block above them.
    const originals = body.querySelectorAll('.product--price-container:not(.aro-qa-price-block)');
    originals.forEach(function (el) { el.style.display = 'none'; });

    let mine = body.querySelector('.aro-qa-price-block');
    if (!mine) {
      mine = document.createElement('div');
      mine.className = 'product--price-container aro-qa-price-block';
      // Insert before the first original so we sit in the same vertical slot.
      const first = originals[0];
      if (first && first.parentNode) {
        first.parentNode.insertBefore(mine, first);
      } else {
        const slot = body.querySelector('.product-information--inner') || body.querySelector('.product-information');
        if (slot) slot.appendChild(mine);
        else return;
      }
    }

    const settings = (window.theme && window.theme.settings) || {};
    const currency = settings.active_currency || 'GBP';
    const vatRate = parseFloat(settings.vat_rate || 0);
    const vatMode = (typeof localStorage !== 'undefined' && localStorage.getItem('taxify-vat-mode')) || 'inclusive';
    // Read tax code from a variant card data attribute (set by Liquid).
    const taxCodeEl = body.querySelector('[data-tax-code]');
    const taxCode = (taxCodeEl && taxCodeEl.dataset && taxCodeEl.dataset.taxCode) || '';
    const isTaxMarket = ['GBP','EUR','BGN','CZK','DKK','HUF','PLN','RON','SEK'].indexOf(currency) !== -1;
    // Shopify variant.price is the EX-VAT base (matches custom-product-price.liquid, which
    // adds VAT for the inc display). So INC = price * (1 + rate); EXC = price as-is.
    // T0 = zero-rated (inc == exc).
    const mult = (taxCode !== 'T0' && vatMode === 'inclusive' && vatRate)
      ? (1 + vatRate)
      : 1;

    const fmt = function (cents) {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format(cents / 100);
    };

    const price = (variant.price || 0) * mult;
    const compare = (variant.compare_at_price || 0) * mult;
    const onSale = compare && compare > price;

    // VAT label — only shown in tax-collecting markets.
    let vatLabel = '';
    if (isTaxMarket) {
      if (taxCode === 'T0') {
        vatLabel = '<small style="font-size:0.55em;color:#666;font-weight:400;margin-left:6px;">(Zero Rated VAT)</small>';
      } else {
        vatLabel = '<small style="font-size:0.55em;color:#666;font-weight:400;margin-left:6px;">' + (vatMode === 'exclusive' ? '(ex VAT)' : '(inc VAT)') + '</small>';
      }
    }

    // Price → From → Save in DOM order.
    let html = '<span class="aro-qa-price" style="display:block;font-size:1.9em;font-weight:600;line-height:1.1;">' + fmt(price) + vatLabel + '</span>';
    if (onSale) {
      html += '<span class="aro-qa-from" style="display:block;text-decoration:line-through;color:#888;margin-top:6px;font-size:1em;"><del>From ' + fmt(compare) + '</del></span>';
      html += '<span class="aro-qa-save" style="display:inline-block;margin-top:8px;background-color:#437B22;color:#fff;font-size:14px;padding:6px 10px;font-weight:600;border-radius:4px;">Save ' + fmt(compare - price) + '</span>';
    }
    mine.innerHTML = html;
  }

  // Find the variant id the user has actually picked inside the modal.
  function resolveSelectedVariantId(form) {
    // Most reliable signal: the variant card with .selected
    const scope = form.closest('.aro-quick-add__pdp') || form;
    const selectedCard = scope.querySelector('.custom-variant-card.selected, [class*="variant-card"].selected');
    if (selectedCard && selectedCard.dataset.variantId) return selectedCard.dataset.variantId;

    // Next: a checked radio inside a card
    const radio = scope.querySelector('.custom-variant-card-wrapper input[type="radio"]:checked, .custom-variant-card input[type="radio"]:checked');
    if (radio) {
      const wrapper = radio.closest('[data-variant-id]');
      if (wrapper && wrapper.dataset.variantId) return wrapper.dataset.variantId;
      if (radio.value && /^\d+$/.test(radio.value)) return radio.value;
    }

    // Final fallback: whatever the form thinks
    const idEl = form.querySelector('[name="id"]');
    if (idEl) return idEl.value;
    return null;
  }

  // Write the resolved variant id back onto the form so /cart/add.js gets it.
  function syncFormVariantId(form, variantId) {
    let idEl = form.querySelector('[name="id"]');
    if (!idEl) {
      idEl = document.createElement('input');
      idEl.type = 'hidden';
      idEl.name = 'id';
      form.appendChild(idEl);
    }
    if (idEl.tagName === 'SELECT') {
      Array.from(idEl.options).forEach((o) => { o.selected = (o.value === String(variantId)); });
      idEl.value = String(variantId);
    } else {
      idEl.value = String(variantId);
    }
  }

  function trimFromStillDeciding(scope) {
    const sidePanel = scope.querySelector('.side-panel-links--container');
    if (!sidePanel) return;
    // Walk up until we hit the product-information container so we remove at
    // block-level rather than half-removing a wrapper.
    let target = sidePanel;
    while (target.parentElement) {
      const parent = target.parentElement;
      if (
        parent === scope ||
        parent.classList.contains('product-information--inner') ||
        parent.classList.contains('product-information')
      ) break;
      target = parent;
    }
    // Remove this block and all following siblings.
    while (target) {
      const next = target.nextElementSibling;
      target.remove();
      target = next;
    }
  }

  function closeModal() {
    if (typeof dialog.close === 'function') {
      try { dialog.close(); } catch (_) { dialog.removeAttribute('open'); }
    } else {
      dialog.removeAttribute('open');
    }
    if (returnFocusEl && typeof returnFocusEl.focus === 'function') {
      try { returnFocusEl.focus(); } catch (_) {}
    }
  }

  async function handleAtcSubmit(e) {
    e.preventDefault();
    const form = e.target.closest('form');
    if (!form) return;

    const submitBtn = form.querySelector('[type="submit"], [name="add"]');
    const originalText = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Adding…'; }

    try {
      // 2026-05-25: the PDP's variant-picker code that normally syncs the form's
      // [name="id"] doesn't always fire inside a modal context. Force it by reading
      // the currently selected variant card and writing its variant id onto the form.
      const selectedVariantId = resolveSelectedVariantId(form);
      if (selectedVariantId) syncFormVariantId(form, selectedVariantId);

      const formData = new FormData(form);
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
        credentials: 'same-origin',
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.description || `HTTP ${r.status}`);
      }

      // Fire whatever cart-drawer events the theme already listens for
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:open'));
      window.dispatchEvent(new CustomEvent('on:cart:add'));

      if (submitBtn) submitBtn.textContent = 'Added ✓';
      setTimeout(closeModal, 600);
    } catch (err) {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = originalText; }
      const existing = form.querySelector('.aro-quick-add__error');
      if (existing) existing.remove();
      const errEl = document.createElement('div');
      errEl.className = 'aro-quick-add__error';
      errEl.textContent = `Couldn't add to cart — ${err.message || 'unknown error'}.`;
      form.appendChild(errEl);
    }
  }
})();
